import { useMemo } from 'react';
import { Send } from 'domains/campaigns/types';
import Autocomplete from 'domains/core/components/Autocomplete';
import {
    ChannelSegmentFilter,
    EngagementUIOperator,
    isChannelSegmentFilterArray,
    SegmentFilter,
    SegmentFilterEngagementFields,
    SegmentFilterState,
    SegmentFilterV2,
} from 'domains/segments/types';
import useSends from 'hooks/queries/useSends';
import { SendJobStatus } from 'models/enums';

export type Props = {
    activeChannelEngagementFilter?: SegmentFilterV2;
    engagementFilters?: SegmentFilter[] | ChannelSegmentFilter[];
    filter: SegmentFilterState<SegmentFilterEngagementFields>;
    index: number;
    sends: Send[];
    updateSegmentFilter: (
        index: number,
        filter: SegmentFilterState<SegmentFilterEngagementFields>,
        resetPreview?: boolean
    ) => void;
};

const SendAutocomplete = ({ engagementFilters, filter, index, sends, updateSegmentFilter }: Props) => {
    const { autocompleteInputValue, autocompleteValue, categorySpecificFields } = filter;

    const activeChannelEngagementFilters: ChannelSegmentFilter['filters'] =
        isChannelSegmentFilterArray(engagementFilters) &&
        engagementFilters.find(
            (engagementFilter: ChannelSegmentFilter) => engagementFilter.channel === categorySpecificFields.channel
        )?.filters;

    const activeChannelEngagementFilter =
        isChannelSegmentFilterArray(engagementFilters) &&
        activeChannelEngagementFilters?.find((engagementFilter) => {
            if (filter.operator === EngagementUIOperator.DID_DELIVER) {
                return engagementFilter.displayName === 'Delivered';
            } else if (filter.operator === EngagementUIOperator.DID_NOT_DELIVER) {
                return engagementFilter.displayName === 'Not Delivered';
            } else {
                return engagementFilter.displayName.toLowerCase() === filter.operator;
            }
        });

    const { data: sendsByChannel } = useSends({
        path: activeChannelEngagementFilter?.sendsRef,
        reactQueryOptions: {
            enabled: Boolean(activeChannelEngagementFilter?.sendsRef),
        },
    });

    const sendsForDropdown = sendsByChannel ?? sends;
    const sendDropdownOptions = useMemo(
        () =>
            sendsForDropdown
                ?.filter((c) => c.status !== 'FAILED' && c.status !== SendJobStatus.CANCELED)
                .map((c) => ({ label: c.name, value: c.id })) ?? [],
        [sendsForDropdown]
    );

    const handleInputValueChange = (newInputValue: string) => {
        updateSegmentFilter(index, {
            ...filter,
            autocompleteInputValue: newInputValue,
        });
    };

    const handleValueChange = (value: any) => {
        updateSegmentFilter(index, {
            ...filter,
            autocompleteValue: value,
            categorySpecificFields: {
                ...categorySpecificFields,
                value: value?.value,
                sendInteractionField: {
                    autocompleteInputValue: '',
                    autocompleteValue: null,
                    value: null,
                },
            },
        });
    };

    const sharedProps = {
        inputValue: autocompleteInputValue ?? '',
        isOptionEqualToValue: (option: { value: string }, value: { value: string }) => option.value === value.value,
        onChange: (_e: any, value: any) => handleValueChange(value),
        onInputChange: (_e: any, newInputValue: string) => handleInputValueChange(newInputValue),
        options: sendDropdownOptions ?? [],
        sx: { width: '33rem' },
    };

    return (
        <Autocomplete
            data-testid="send-autocomplete"
            data-test="send-autocomplete"
            label="Send"
            placeholder="Enter a send name"
            type="string"
            value={autocompleteValue ?? null}
            {...sharedProps}
        />
    );
};

export default SendAutocomplete;
